import React, {useMemo} from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import {useSnackbar} from 'notistack';
import {Cancel, Check} from '@mui/icons-material';
import {gql, useQuery} from '@apollo/client';
import {useRequestAtr, useCheckFactFindPermissions} from './permissionHooks';
import AutoField, {ActionType} from '../AutoForm/AutoFieldNew';
import withAutoFormNew from '../AutoForm/AutoFormNew';
import {useAutoForm} from '../AutoForm/useAutoForm';
import {StaffDetails} from '../../schema-types/StaffDetails';
import {
  AccountSearchInput,
  useAccountSearch,
} from '../AccountSearchInput/AccountSearchInput';
import {CanViewContact, CanViewContactVariables} from '../../schema-types/CanViewContact';
import {useRTQ} from '../../hooks/useRTQ';

type PermissionDialogProps = {
  open: boolean;
  selectedStaff: StaffDetails | null;
  onClose: () => void;
};

export type PermissionsFormValue = {
  accountId: string;
  contactId: string;
  staffId: string;
  searchTerm: string;
};

const CanViewThis: React.FC<{
  canView: boolean | null;
  name?: string | null;
  type: 'account' | 'contact';
  loading: boolean;
}> = ({canView, name = '', type, loading}) => {
  if (loading) {
    return (
      <CircularProgress
        size={24}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          mt: -1.5,
          ml: -1.5,
        }}
      />
    );
  }
  const modalVerb = canView ? 'can' : 'cannot';
  return (
    <Box mb={1} display="flex" justifyContent="flex-start" alignItems="center">
      {canView ? <Check color="success" /> : <Cancel color="error" />}
      {`${name} ${modalVerb} view ${type}`}
    </Box>
  );
};

function PermissionForm({selectedStaff}: Pick<PermissionDialogProps, 'selectedStaff'>) {
  const {enqueueSnackbar} = useSnackbar();
  const {account, searchField, searchLoading} = useAccountSearch('accountId');
  const {values} = useAutoForm<PermissionsFormValue>();
  const {atrName} = useRTQ();

  const contact = useMemo(() => {
    if (!account) {
      return null;
    }
    if (account.primaryContact?.id === values.contactId) {
      return account.primaryContact;
    }
    if (account.secondaryContact?.id === values.contactId) {
      return account.secondaryContact;
    }
    return null;
  }, [account, values.contactId]);

  const contactIsTest = useMemo(() => {
    if (contact?.name?.toLocaleLowerCase()?.includes('test')) {
      return true;
    }
    if (account?.name?.toLocaleLowerCase()?.includes('test')) {
      return true;
    }
    return false;
  }, [contact, account]);

  const contactOptions = useMemo(() => {
    if (account?.primaryContact && account?.secondaryContact) {
      return [
        {
          value: account.primaryContact.id,
          label: `Primary: ${account.primaryContact.name}`,
        },
        {
          value: account.secondaryContact.id,
          label: `Secondary: ${account.secondaryContact.name}`,
        },
      ];
    }
    if (account?.primaryContact) {
      return [
        {
          value: account.primaryContact.id,
          label: `Primary: ${account.primaryContact.name}`,
        },
      ];
    }
    return [];
  }, [account]);

  const {data, loading} = useQuery<CanViewContact, CanViewContactVariables>(
    gql`
      query CanViewContact($staffId: UUID!, $contactId: UUID!, $accountId: UUID!) {
        canViewAccount: checkStaffCanViewEntity(
          entityType: ACCOUNT
          entityId: $accountId
          staffId: $staffId
        )
        canViewContact: checkStaffCanViewEntity(
          entityType: CONTACT
          entityId: $contactId
          staffId: $staffId
        )
      }
    `,
    {
      variables: {
        accountId: account?.id,
        staffId: selectedStaff?.id,
        contactId: contact?.id,
      },
      skip: !account?.id || !contact?.id || !selectedStaff?.id,
    }
  );

  const staffId = selectedStaff?.id || '';
  const {mutateFunction, loading: atrLoading} = useRequestAtr({
    accountId: account?.id || '',
    contactId: values.contactId || '',
    staffId,
  });
  const {checkFactFindLoading, onCheckFactFind} = useCheckFactFindPermissions();
  type SaltusAny = any;

  const handlePermissionRequest = async () => {
    try {
      const result = await mutateFunction();
      if (result.data?.requestAtr?.ok) {
        enqueueSnackbar(`${atrName} successfully completed', {variant: 'success`);
      } else {
        const errors: (string | null)[] = [];
        if (result.errors?.length) {
          errors.push(...result.errors.map((x) => x.message));
        }
        if (result.data?.requestAtr?.errors?.length) {
          errors.push(...(result.data?.requestAtr?.errors || []));
        }
        enqueueSnackbar(`Failed to complete ${atrName} ${errors.join(', ')}`, {
          variant: 'error',
        });
      }
    } catch (error: SaltusAny) {
      console.error('Mutation error:', error);

      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      enqueueSnackbar(`Mutation failed: ${message}`, {variant: 'error'});
    }
  };

  const staffCanViewAccount = useMemo(() => {
    return data?.canViewAccount ?? null;
  }, [data, account]);

  const staffCanViewContact = useMemo(() => {
    return data?.canViewContact ?? null;
  }, [data, contact]);

  return (
    <>
      <Box mb={2} sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        <DialogContentText>
          Check permissions for staff member {selectedStaff?.name} to use a specific
          account:
        </DialogContentText>
        <AccountSearchInput
          key="account-search"
          account={account}
          searchField={searchField}
          searchLoading={searchLoading}
        />
        {contactOptions.length > 0 && (
          <AutoField
            field={{
              id: 'contactId',
              type: ActionType.select,
              label: 'Contact',
              initialValue: '',
              options: contactOptions,
              disabled: searchLoading,
            }}
            data-testid="contact-select"
          />
        )}
      </Box>
      {contact || account ? (
        <Box p={1}>
          <Typography mb={1} variant="h6">
            Permissions:
          </Typography>
          <CanViewThis
            type="account"
            canView={staffCanViewAccount}
            name={selectedStaff?.name}
            loading={loading}
          />
          {contact ? (
            <CanViewThis
              type="contact"
              canView={staffCanViewContact}
              name={selectedStaff?.name}
              loading={loading}
            />
          ) : null}
        </Box>
      ) : null}
      {contact ? (
        <>
          {!contactIsTest && contact ? (
            <Box mb={2} mt={2} p={2} sx={{bgcolor: 'lightcoral'}}>
              Note: ATRs and FactFinds can only be sent to test accounts
            </Box>
          ) : (
            <Box display="flex" justifyContent="space-around" mt={3} mb={2}>
              <Button
                onClick={handlePermissionRequest}
                color="secondary"
                variant="contained"
                disabled={
                  atrLoading ||
                  !contact ||
                  !staffCanViewAccount ||
                  !staffCanViewContact ||
                  !contactIsTest
                }
                data-testid="request-atr-button"
              >
                {atrLoading ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      mt: -1.5,
                      ml: -1.5,
                    }}
                  />
                ) : (
                  `Request ${atrName}`
                )}
              </Button>
              <Button
                onClick={() => {
                  if (contact?.id) {
                    onCheckFactFind({contactId: contact.id, staffId});
                  }
                }}
                color="secondary"
                variant="contained"
                disabled={
                  checkFactFindLoading ||
                  !contact ||
                  !staffCanViewAccount ||
                  !staffCanViewContact ||
                  !contactIsTest
                }
                data-testid="request-factfind-button"
              >
                {checkFactFindLoading ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      mt: -1.5,
                      ml: -1.5,
                    }}
                  />
                ) : (
                  'Request Fact Find'
                )}
              </Button>
            </Box>
          )}
        </>
      ) : null}
    </>
  );
}

export const PermissionFormWrapped = withAutoFormNew<
  PermissionsFormValue,
  Pick<PermissionDialogProps, 'selectedStaff'>
>(PermissionForm, undefined, {debugPosition: 'bottom'});

export const PermissionDialog: React.FC<PermissionDialogProps> = ({
  onClose,
  open,
  selectedStaff,
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle data-testid="dialog-title">Permissions</DialogTitle>
      <DialogContent sx={{position: 'relative'}} data-testid="dialog-content">
        <PermissionFormWrapped formFields={[]} selectedStaff={selectedStaff} />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => onClose()}
          color="primary"
          data-testid="close-dialog-button"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
